<template>
  <!-- 信标导入弹窗 -->
  <a-modal
    :title="title"
    :visible="open"
    :confirm-loading="uploading"
    @cancel="importExcelHandleCancel"
    @ok="importExcelHandleChange"
  >
    <!-- 上传加载状态 -->
    <a-spin tip="上传中..." :spinning="uploading">
      <!-- 文件拖拽上传组件 -->
      <a-upload-dragger
        name="file"
        accept=".xlsx, .xls"
        :file-list="fileList"
        :multiple="false"
        :remove="handleRemove"
        :before-upload="beforeUpload"
      >
        <!-- 上传图标 -->
        <p class="ant-upload-drag-icon">
          <a-icon type="inbox" />
        </p>
        <!-- 上传提示文字 -->
        <p class="ant-upload-text">请将文件拖拽到此处上传</p>
        <!-- 上传说明文字 -->
        <p class="ant-upload-hint">支持单个上传，也可以点击后选择文件上传</p>
      </a-upload-dragger>
      <!-- 下载模板链接 -->
      <!-- <a @click="importTemplate" v-hasPermi="['system:beacon:importTemplate']"
        >下载模板</a
      > -->
    </a-spin>
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
/**
 * 信标导入Excel组件
 * 功能：提供信标数据的Excel导入功能
 * - 支持拖拽上传和点击上传
 * - 导入前需要选择项目ID
 * - 导入成功后刷新列表
 */
import { importBeaconData } from '@/api/system/beacon'
import { showImportResult, isImportSuccessCode } from '@/utils/importResult'

export default {
  name: 'BeaconImportExcel',
  data() {
    return {
      // 弹窗标题
      title: '信标导入',
      // 弹窗显示/隐藏状态
      open: false,
      // 文件列表（只支持单个文件）
      fileList: [],
      // 上传中状态
      uploading: false,
      // 是否支持更新（0-不支持，1-支持）
      updateSupport: 0,
    }
  },
  methods: {
    /**
     * 取消导入操作
     * 关闭弹窗，清空文件列表，触发ok事件刷新列表
     */
    importExcelHandleCancel() {
      this.open = false
      this.fileList = []
      $emit(this, 'ok')
    },

    /**
     * 打开导入弹窗
     * 在打开弹窗前检查项目ID是否已选择
     */
    importExcelHandleOpen() {
      // 检查项目ID是否已选择
      const currentProjectId = this.$store.getters.currentProjectId
      if (!currentProjectId) {
        this.$message.warning('请先选择项目', 3)
        return
      }
      // 打开弹窗
      this.open = true
    },

    /**
     * 文件上传前的处理
     * 阻止自动上传，手动控制上传流程
     * @param {File} file - 选择的文件对象
     * @returns {Boolean} false-阻止自动上传
     */
    beforeUpload(file) {
      // 将文件添加到文件列表
      this.fileList = [file]
      // 返回false阻止自动上传，由手动控制
      return false
    },

    /**
     * 执行导入操作
     * 1. 检查项目ID是否已选择
     * 2. 检查是否选择了文件
     * 3. 构建FormData并发送导入请求
     * 4. 处理导入结果
     */
    importExcelHandleChange() {
      // 1. 检查项目ID是否已选择
      const currentProjectId = this.$store.getters.currentProjectId
      if (!currentProjectId) {
        this.$message.warning('请先选择项目', 3)
        return
      }

      // 2. 检查是否选择了文件
      if (!this.fileList || this.fileList.length === 0) {
        this.$message.warning('请先选择要导入的文件', 3)
        return
      }

      // 3. 开始上传，显示加载状态
      this.uploading = true

      // 4. 构建FormData表单数据
      const formData = new FormData()
      formData.append('file', this.fileList[0]) // 上传的文件
      formData.append('updateSupport', this.updateSupport) // 是否支持更新
      formData.append('projectId', currentProjectId) // 项目ID

      // 5. 发送导入请求
      importBeaconData(formData)
        .then((response) => {
          // 检查响应是否存在
          if (!response) {
            this.$message.error('导入失败，请稍后重试')
            this.uploading = false
            return
          }
          
          // 导入成功（支持 code 为 1 或 20001，可能是字符串或数字）
          // 使用 == 进行宽松比较，支持字符串和数字
          const code = response.code
          if (isImportSuccessCode(code)) {
            showImportResult(response)
            // 关闭弹窗
            this.open = false
            // 清空文件列表
            this.fileList = []
            // 触发ok事件，刷新列表
            $emit(this, 'ok')
          } else {
            // 导入失败，显示错误信息
            this.$message.error(response.message || response.msg || '导入失败')
          }
        })
        .catch((error) => {
          // 请求异常，显示错误提示
          console.error('导入失败:', error)
          // 如果 error 有 response 数据，尝试获取错误信息
          let errorMsg = '导入失败，请稍后重试'
          if (error && error.response && error.response.data) {
            errorMsg = error.response.data.message || error.response.data.msg || errorMsg
          } else if (error && error.message) {
            errorMsg = error.message
          }
          this.$message.error(errorMsg)
        })
        .finally(() => {
          // 无论成功或失败，都要关闭加载状态
          this.uploading = false
        })
    },

    /**
     * 移除文件
     * 清空文件列表，关闭上传状态
     */
    handleRemove() {
      this.fileList = []
      this.uploading = false
    },
  },
  emits: ['ok'],
}
</script>

<style>
/* Modal 按钮样式 - 全局样式确保生效 */
.ant-modal-footer .ant-btn {
  border-radius: 4px !important;
}

/* 取消按钮样式 */
.ant-modal-footer .ant-btn-default {
  border-radius: 4px !important;
}

/* 确定按钮样式 */
.ant-modal-footer .ant-btn-primary {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  border-radius: 4px !important;
  color: #FFFFFF !important;
}

.ant-modal-footer .ant-btn-primary:hover:not(:disabled) {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #FFFFFF !important;
}

.ant-modal-footer .ant-btn-primary:focus:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

.ant-modal-footer .ant-btn-primary:active:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}
</style>
